import {
  ServiceContext,
  Survey,
  SurveyControls,
  SurveyQuestion,
  useSurveyContext,
  useQuestionLayout,
} from '@confer/lib'
import { getColours, hexToRgb } from '@confer/lib/utils'
import { Spin } from 'antd'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import GridLayout from './GridLayout'
import CompletionNavigation from './CompletionNavigation'

interface SurveyScrollState {
  isSurveyScrolled: boolean
  scrollUiHasChanged: boolean
}

function getBackgroundColourRGB(survey: Survey) {
  const { backgroundColour } = getColours(survey.settings)
  return hexToRgb(backgroundColour)
}

/** Optional custom progress (0–100). Surveys can add getProgressPercentage to settings to shape the bar (e.g. "hope" curve). */
function getDisplayPercentage(
  survey: Survey,
  currentQuestionId: string | undefined,
  rawPercentage: number,
): number {
  const custom = (
    survey.settings as { getProgressPercentage?: (i: number, total: number) => number }
  ).getProgressPercentage
  if (!custom || !currentQuestionId) return rawPercentage
  const currentIndex = survey.questions.findIndex((q) => q.id === currentQuestionId)
  const totalSteps = Math.max(0, survey.questions.length - 1)
  if (totalSteps <= 0) return rawPercentage
  const value = custom(currentIndex, totalSteps)
  return Math.round(Math.min(100, Math.max(0, value)))
}

const SurveyLayout = () => {
  const surveyContentRef = useRef<HTMLDivElement>(null)
  const {
    survey,
    submissionId,
    currentQuestion,
    loading,
    error,
    interactionDisabled,
    currentPercentage,
    sectionNavigationItems,
    navigateToSection,
    sectionNavigationFocusKey,
  } = useSurveyContext()
  const layout = useQuestionLayout(currentQuestion)
  const { databaseService, messagerService: messager, loggingService } = useContext(ServiceContext)
  const [surveyScrollState, setSurveyScrollState] = useState<SurveyScrollState>({
    isSurveyScrolled: false,
    scrollUiHasChanged: false,
  })
  const backgroundRGB = getBackgroundColourRGB(survey)
  const { primaryColour } = getColours(survey.settings)
  const urlParams = new URLSearchParams(window.location.search)
  const lang = urlParams.get('lang') ?? 'en'
  const displayPercentage = getDisplayPercentage(survey, currentQuestion?.id, currentPercentage)
  const lastNavigationKey = useRef(sectionNavigationFocusKey)
  const focusPending = useRef(false)
  useEffect(() => {
    if (lastNavigationKey.current !== sectionNavigationFocusKey) {
      lastNavigationKey.current = sectionNavigationFocusKey
      focusPending.current = true
    }
    if (focusPending.current && !loading && currentQuestion?.type !== 'hidden') {
      surveyContentRef.current?.focus()
      focusPending.current = false
    }
  }, [sectionNavigationFocusKey, currentQuestion?.id, currentQuestion?.type, loading])

  const handleTouchMove = (e: React.UIEvent<HTMLDivElement>) => {
    const currentScrollTop = e.currentTarget.scrollTop
    const isSurveyScrolled = currentScrollTop > 0

    setSurveyScrollState((state) => ({
      isSurveyScrolled: state.scrollUiHasChanged ? state.isSurveyScrolled : isSurveyScrolled,
      scrollUiHasChanged: state.scrollUiHasChanged || isSurveyScrolled,
    }))
  }

  const saveForLater = useCallback(async () => {
    try {
      if (!submissionId) {
        messager.error('Cannot save: submission not initialised yet.')
        return
      }
      const email = window.prompt('Enter your email address to receive a resume link:')
      if (!email) {
        return
      }
      await databaseService.generateResumeLink(survey.settings.surveyName, submissionId, email)
      messager.success('A resume link has been sent to your email.')
    } catch (e) {
      const error = e as Error
      loggingService.error('Error generating resume link', { error })
      messager.error('Error sending resume link. Please try again.')
    }
  }, [submissionId, databaseService, survey.settings.surveyName])

  return (
    <GridLayout
      settings={survey.settings}
      question={currentQuestion ?? survey.questions[0]}
      currentPercentage={displayPercentage}
      isSurveyScrolled={surveyScrollState.isSurveyScrolled}
      headerAction={
        survey.settings.allowEmailResume ? (
          <SaveForLaterButton type="button" $colour={primaryColour} onClick={saveForLater}>
            Save for later
          </SaveForLaterButton>
        ) : undefined
      }
      sectionNavigation={
        sectionNavigationItems.length
          ? { items: sectionNavigationItems, onNavigate: navigateToSection }
          : undefined
      }
    >
      {survey.settings.showQid ? <QuestionId>{currentQuestion?.id}</QuestionId> : null}
      {loading ? (
        <>
          <Spin />
          <ErrorText>
            {error &&
              `Oops! There was an error connecting to the survey. If you know who is running the survey, please let them know something is wrong (${error.message}).`}
          </ErrorText>
        </>
      ) : (
        <SurveyContentContainer
          ref={surveyContentRef}
          tabIndex={-1}
          onTouchMove={handleTouchMove}
          onTouchStart={() => {
            setSurveyScrollState((state) => ({ ...state, scrollUiHasChanged: false }))
          }}
          backgroundColourRGB={backgroundRGB}
          fontFamily={survey.settings.font?.family}
          noScrollShadows={currentQuestion?.type === 'welcomePage'}
          style={layout.viewportStyle}
          lang={lang}
          key={currentQuestion?.id}
        >
          <SurveyQuestion />
        </SurveyContentContainer>
      )}

      <SurveyControls />
      <CompletionNavigation />
      {interactionDisabled && <DisableInteractionOverlay />}
    </GridLayout>
  )
}

export const appear = keyframes`
   0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

const SaveForLaterButton = styled.button<{ $colour: string }>`
  min-height: 44px;
  padding: 0.5rem 1rem;
  border: 1px solid ${(props) => props.$colour};
  border-radius: 2rem;
  background: white;
  color: ${(props) => props.$colour};
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--off-light, #f5f5f5);
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.$colour};
    outline-offset: 2px;
  }
`

interface SurveyContentProps {
  // settings: SurveySettings
  fontFamily?: string
  backgroundColourRGB: { r: number; g: number; b: number }
  noScrollShadows?: boolean
}

const SurveyContentContainer = styled.div<SurveyContentProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: auto;
  scrollbar-gutter: stable both-edges;
  /* @media (max-width: 479px) {
    scrollbar-gutter: auto;
  } */
  animation: 0.5s ${appear} ease;
  font-family: ${(props) =>
    props.fontFamily ? `'${props.fontFamily}', 'Rethink Sans', sans-serif` : 'inherit'};

  /* Top/bottom shadows when content overflows - https://lea.verou.me/2012/04/background-attachment-local/ */
  @media (min-width: 480px) {
    background: ${({ backgroundColourRGB, noScrollShadows }) => {
      const { r, g, b } = backgroundColourRGB
      const bg = `rgba(${r}, ${g}, ${b}, 1)`
      const bgTrans = `rgba(${r}, ${g}, ${b}, 0)`
      const shadow = 'rgba(41, 50, 56, 0.5)'
      return `
    linear-gradient(${bg} 30%, ${bgTrans}) center top,
    linear-gradient(${bgTrans}, ${bg} 70%) center bottom,
    radial-gradient(farthest-side at 50% 0, ${shadow}, rgba(0,0,0,0)) center top,
    radial-gradient(farthest-side at 50% 100%, ${shadow}, rgba(0,0,0,0)) center bottom
    `
    }};
    background-repeat: no-repeat;
    background-size:
      100% 40px,
      100% 40px,
      100% 14px,
      100% 14px;
    background-attachment: local, local, scroll, scroll;
  }

  @media (max-width: 479px) {
    background: ${({ backgroundColourRGB, noScrollShadows }) => {
      if (noScrollShadows) {
        return ''
      }
      const { r, g, b } = backgroundColourRGB
      const bg = `rgba(${r}, ${g}, ${b}, 1)`
      const bgTrans = `rgba(${r}, ${g}, ${b}, 0)`
      const shadow = 'rgba(41, 50, 56, 0.5)'
      return `
    linear-gradient(${bg} 30%, ${bgTrans}) center top,
    linear-gradient(${bgTrans}, ${bg} 70%) center bottom,
    radial-gradient(farthest-side at 50% 0, ${shadow}, rgba(0,0,0,0)) center top,
    radial-gradient(farthest-side at 50% 100%, ${shadow}, rgba(0,0,0,0)) center bottom
    `
    }};
    background-repeat: no-repeat;
    background-size:
      100% 40px,
      100% 40px,
      100% 14px,
      100% 14px;
    background-attachment: local, local, scroll, scroll;
  }
`

const QuestionId = styled.p`
  color: red;
  position: absolute;
  top: 30px;
  right: 10%;
`

const ErrorText = styled.div`
  margin: 1rem;
`

const DisableInteractionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
`

export default SurveyLayout
